<template>
  <div class="items-show-container">
    <PageNavigationBar v-if="!isItemSkuLoading" />
    <div class="feedback">
      <transition name="fade" @enter="enterSuccessFeedback">
        <FeedbackSuccess
          v-if="showableSuccessFeedback"
          :message="successMessage"
        />
      </transition>
      <transition name="fade" @enter="enterErrorFeedback">
        <FeedbackError v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <div class="items-show__body">
      <div :id="pageNavigationIds.GIFT_INFO" class="items-show__main-wrapper">
        <div v-if="isItemSkuLoading" class="item-sku-loading">
          <SmallLoading />
        </div>
        <div v-else class="items-show__main">
          <div class="items-show__image-area">
            <ImgLazy
              :image-url="itemSku.imageUrl"
              :alt-text="itemSku.itemName"
              aspect-ratio="1 / 1"
            />
            <div class="items-show__navigation-wrapper">
              <LimitedTimeSalesInfo
                :ended-at="itemSku.limitedTimeSalesEndedAt"
              />
              <template v-if="!isItemSkuLoading && hasGiftCodeCampaigns">
                <CampaignNavigationArea
                  v-for="giftCodeCampaign in giftCodeCampaigns"
                  :key="giftCodeCampaign.id"
                  :message="giftCodeCampaign.message"
                  :campaign-detail-id="
                    `gift-code-campaign-detail-${giftCodeCampaign.id}`
                  "
                />
              </template>
              <template v-if="!isItemSkuLoading && hasStandardCampaigns">
                <CampaignNavigationArea
                  v-for="standardCampaign in standardCampaigns"
                  :key="standardCampaign.id"
                  :message="standardCampaign.message"
                  :campaign-detail-id="
                    `standard-campaign-detail-${standardCampaign.id}`
                  "
                />
              </template>
            </div>
          </div>
          <div class="items-show__form-area">
            <div class="items-show__description-area">
              <div
                v-if="brand.listDisplayableItemPresent"
                class="description-area__brand-name text-link"
                @click="moveToBrandDetail"
              >
                <span>{{ itemSku.brandName }}</span>
              </div>
              <div v-else class="description-area__brand-name subhead">
                <span>{{ itemSku.brandName }}</span>
              </div>
              <div class="description-area__item-name title">
                <span>{{ itemSku.itemName }}</span>
              </div>
              <div class="description-area__item-sku-price-area">
                <div class="description-area__item-sku-price headline">
                  <span>{{ itemSkuPrice }}</span>
                </div>
                <span
                  v-if="itemSku.isIncludedTax"
                  class="description-area__tax-text"
                >
                  税込
                </span>
              </div>
              <div class="description-area__expired-date-area">
                <div class="description-area__expired-date-title caption">
                  <span>有効期限</span>
                </div>
                <div class="description-area__expired-date caption">
                  <span>{{ itemSku.expiredDate }}</span>
                </div>
              </div>
              <div class="description-area__usage-instruction-area">
                <div class="description-area__usage-instruction-title caption">
                  <span>ギフトの利用方法</span>
                </div>
                <div class="description-area__usage-instruction-link">
                  <span
                    class="description-area__usage-instruction-link-text"
                    @click="openUsageInstructionModal"
                  >
                    {{ itemSku.usageInstructionTitle }}
                  </span>
                </div>
              </div>
              <div
                v-if="isDisabledPaymentMethods"
                class="description-area__disabled-payment-method box-list--attention"
              >
                <span>
                  このギフトは{{
                    itemSku.disabledPaymentMethodNames.join('、')
                  }}をご利用いただけません
                </span>
              </div>
            </div>
            <div class="items-show__action-button-area">
              <div class="items-show__action-button">
                <div @click="toggleBookmarkAction">
                  <div class="items-show__action-button__icon">
                    <BookmarkFillPrimary v-if="itemSku.bookmarked" />
                    <BookmarkOutline v-else />
                  </div>
                  <span :class="bookmarkButtonClass">
                    ブックマーク
                  </span>
                </div>
              </div>
              <div class="items-show__action-button">
                <div @click="toggleWishListAction">
                  <div class="items-show__action-button__icon">
                    <HeartFillPrimairy v-if="itemSku.wishListed" />
                    <HeartOutline v-else />
                  </div>
                  <span :class="wishListButtonClass">
                    ほしいもの
                  </span>
                </div>
              </div>
              <div
                class="items-show__action-button"
                @click="onClickShareButton"
              >
                <div class="items-show__action-button__icon">
                  <ShareOutline />
                </div>
                <span class="items-show__action-button__text">
                  シェア
                </span>
              </div>
            </div>
            <div class="items-show__ticket-count-area">
              <div
                v-if="hasSeveralItemSkus"
                class="ticket-count-area__item-sku-area"
              >
                <div class="ticket-count-area__selected-item-sku-name caption">
                  <span>選択中: {{ itemSku.name }}</span>
                </div>
                <div class="ticket-count-area__select-boxes--horizontal-scroll">
                  <HorizontalScroll
                    ref="select-box-horizontal"
                    ref-name="select-boxes"
                  >
                    <SelectBoxes
                      :items-name="'select_box'"
                      :items="selectBoxItemSkus"
                      :selected-value="itemSku.id"
                      :scrollable="false"
                      @input="selectRadioBtn"
                      @mount="onMountedSelectBoxes"
                    />
                  </HorizontalScroll>
                </div>
              </div>
              <div class="ticket-count-area__stepper-control-area">
                <div class="ticket-count-area__stepper-control-title subhead">
                  <span>枚数</span>
                </div>
                <div
                  class="ticket-count-area__stepper-control-box stepper-control__box"
                >
                  <span
                    :class="{
                      'stepper-control__btn--minus': !isMinusBtnActive,
                      'stepper-control__btn--minus--active': isMinusBtnActive
                    }"
                    @click="countDown"
                  />
                  <span class="stepper-control__number">
                    {{ itemSkuCount }}
                  </span>
                  <span
                    :class="{
                      'stepper-control__btn--plus': !isPlusBtnActive,
                      'stepper-control__btn--plus--active': isPlusBtnActive
                    }"
                    @click="countUp"
                  />
                </div>
              </div>
            </div>
            <div
              v-if="isWishListUserPresent"
              class="items-show__wish-list-user-area"
            >
              <AccountIconWh40
                :image-url="wishListUserIconUrl"
                class="items-show__wish-list-user-icon"
              />
              <span class="items-show__wish-list-user-nickname">
                {{ wishListUserNickname }}
              </span>
              <span class="items-show__wish-list-user-for-label">
                さんへ
              </span>
            </div>
            <div class="items-show__button-area">
              <div
                v-if="itemSku.isOutOfStock"
                class="error-message button-area__out-of-stock-message"
              >
                <span>選択中のギフトは入荷待ちです</span>
              </div>
              <button
                class="btn btn--primary button-area__send-gift-button"
                :disabled="disableSendGiftButton || null"
                @click="submitSendGiftButton"
              >
                ギフトを贈る
              </button>
              <button
                v-if="!isWishListUserPresent"
                class="btn btn--secondary button-area__personal-use-button"
                :disabled="disablePersonalUseButton || null"
                @click="togglePersonalUseButton"
              >
                自分用に購入する
              </button>
              <div
                v-if="isWishListUserPresent"
                class="items-show__back-to-wish-list-area"
              >
                <a
                  class="items-show__back-to-wish-list-link text-link--sub"
                  :href="wishListUrl"
                >
                  ほしいものリストに戻る
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="!isItemSkuLoading" class="items-show__sub-wrapper">
        <div class="items-show__bulk-order-sub-content">
          <div class="items-show__bulk-order-area p-16">
            <ImgLazy
              :image-url="bulkOrderSuggestImagePath"
              :alt-text="まとめてオーダー"
              aspect-ratio="8 / 5"
              class="items-show__bulk-order-area--icon"
            />
            <p class="items-show__bulk-order-area--text">
              <!-- eslint-disable-next-line prettier/prettier 改行がない場合に空白が入らないよう1行にまとめる -->
              11名以上のお相手にギフトカードをまとめて購入したい方は「まとめてオーダー」がおすすめです。<span class="items-show__bulk-order-area--text-link" data-gtm-click="bulk_order_modal" :data-gtm-item-id="itemId" @click="openBulkOrderSuggestModal">詳しく見る</span>
            </p>
          </div>
        </div>
      </div>
      <GiftDetailArea
        :is-loading="isItemSkuLoading"
        :description-items="itemSku.descriptionItems"
        :mail-slot-deliverable="itemSku.mailSlotDeliverable"
        :description-html="itemSku.descriptionHtml"
        :staff-reviews="itemSku.staffReviews"
      />
      <div
        v-if="
          !isItemSkuLoading && (hasGiftCodeCampaigns || hasStandardCampaigns)
        "
        class="items-show__campaign-detail-wrapper items-show__sub-wrapper"
      >
        <div
          v-if="hasGiftCodeCampaigns"
          class="items-show__gift-code-campaign-detail-wrapper"
        >
          <GiftCodeCampaignDetailArea
            v-for="giftCodeCampaign in giftCodeCampaigns"
            :key="giftCodeCampaign.id"
            :gift-code-campaign-id="
              `gift-code-campaign-detail-${giftCodeCampaign.id}`
            "
            :title="giftCodeCampaign.title"
            :banner-image-url="giftCodeCampaign.banner_image_url"
            :banner-url="giftCodeCampaign.banner_url"
            :expiration-date="giftCodeCampaign.expiration_date"
            :stock-count="giftCodeCampaign.stock_count"
            :discount-rate="giftCodeCampaign.discount_rate"
            :discount-price="giftCodeCampaign.discount_price"
            :serial="giftCodeCampaign.serial"
            :attention="giftCodeCampaign.attention"
            @open-attention-modal="openGiftCodeAttentionModal"
            @copy-success="handleCopySuccess"
            @copy-error="handleCopyError"
          />
        </div>
        <div
          v-if="hasStandardCampaigns"
          class="items-show__standard-campaign-detail-wrapper"
        >
          <StandardCampaignDetailArea
            v-for="standardCampaign in standardCampaigns"
            :key="standardCampaign.id"
            :standard-campaign-id="
              `standard-campaign-detail-${standardCampaign.id}`
            "
            :title="standardCampaign.title"
            :banner-image-url="standardCampaign.banner_image_url"
            :banner-url="standardCampaign.banner_url"
            :ended-at="standardCampaign.ended_at"
            :description="standardCampaign.description"
            :attention="standardCampaign.attention"
            :button-text="standardCampaign.button_text"
            :button-url="standardCampaign.button_url"
            @open-attention-modal="openStandardCampaignAttentionModal"
          />
        </div>
      </div>
      <SpecificationArea
        v-if="!isItemSkuLoading"
        :food-and-beverage-detail="itemSku.foodAndBeverageDetail"
        :set-contents="itemSku.setContents"
        :capacity-size-weight="itemSku.capacitySizeWeight"
        :other-specifications="itemSku.otherSpecifications"
      />
      <NoticeArea
        v-if="!isItemSkuLoading"
        :notice-detail="itemSku.noticeDetail"
      />
      <div class="items-show__brand-info-wrapper items-show__sub-wrapper">
        <div class="items-show__brand-info-content items-show__sub-content">
          <DecorationHead text="ブランド情報" :heading-level="2" />
          <div v-if="isBrandLoading" class="item-sku-loading">
            <SmallLoading />
          </div>
          <div v-else class="brand-info-content__body">
            <div class="brand-info-content__image-area">
              <ImgLazy
                :image-url="brand.imageUrl"
                :alt-text="brand.name"
                aspect-ratio="1 / 1"
                :width="80"
                :height="80"
                class="brand-info-content__image"
              />
              <div class="brand-info-content__brand-name body-bold">
                <span>{{ brand.name }}</span>
              </div>
            </div>
            <div class="brand-info-content__brand-description">
              <span>{{ brand.description }}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="showableOtherItemsSection"
        class="items-show__other-items-wrapper items-show__sub-wrapper"
      >
        <div class="items-show__other-items-content items-show__sub-content">
          <DecorationHead text="このブランドの他のギフト" :heading-level="2" />
          <div v-if="isOtherItemsLoading" class="item-sku-loading">
            <SmallLoading />
          </div>
          <div v-else class="other-items-content__body">
            <div class="other-items-content__horizontal-scroll">
              <HorizontalScroll ref-name="other-items-content">
                <ItemBoxes :items="brandItems" :scrollable="false" />
              </HorizontalScroll>
            </div>
          </div>
        </div>
      </div>
      <div class="items-show__back-to-top">
        <span class="back-to-top-link subhead" @click="onClickBackToTop">
          ページトップへ ▲
        </span>
      </div>
    </div>
    <TextModalWithSecondary
      v-if="isWishListModalActive"
      :title-text="'作成中のギフトカードがあります'"
      :caption-text="
        '作成できるカードは１つまでです。\n内容を破棄して新規作成しますか?'
      "
      :primary-button-text="'新規作成する'"
      :secondary-button-text="'カードを確認する'"
      :cancel-button-text="'閉じる'"
      @clicked-modal-cover="closeWishListModal"
      @clicked-primary="createGiftCardWithWishList"
      @clicked-secondary="moveToGiftCardEditPage"
      @clicked-cancel="closeWishListModal"
    />
    <TextModalWithSecondary
      v-if="isLoginModalActive"
      :title-text="'ほしいものリストへの追加は\n会員登録が必要です'"
      :primary-button-text="'新規登録'"
      :secondary-button-text="'ログイン'"
      :cancel-button-text="'閉じる'"
      @clicked-modal-cover="closeLoginModal"
      @clicked-primary="moveToRegistration"
      @clicked-secondary="moveToLogin"
      @clicked-cancel="closeLoginModal"
    />
    <RecommendBulkOrderModal
      v-if="isBulkOrderSuggestModalActive"
      :item-id="itemId"
      @clicked-modal-cover="closeBulkOrderSuggestModal"
      @clicked-close="closeBulkOrderSuggestModal"
    />
    <UsageInstructionModal
      v-if="isUsageInstructionModalActive"
      :usage-instruction-title="itemSku.usageInstructionTitle"
      :usage-instruction-description="itemSku.usageInstructionDescription"
      @clicked-modal-cover="closeUsageInstructionModal"
      @clicked-close="closeUsageInstructionModal"
    />
    <CampaignAttentionModal
      v-if="isGiftCodeAttentionModalActive"
      title-text="ギフトコード利用時の注意事項"
      :attention="currentGiftCodeCampaignAttention"
      @close="closeGiftCodeAttentionModal"
    />
    <CampaignAttentionModal
      v-if="isStandardCampaignAttentionModalActive"
      title-text="キャンペーン参加時の注意事項"
      :attention="currentStandardCampaignAttention"
      @close="closeStandardCampaignAttentionModal"
    />
  </div>
</template>

<script>
import axios from 'axios'
import SelectBoxes from '../../molecules/select_boxes/SelectBoxes.vue'
import SmallLoading from '../../atoms/loadings/SmallLoading.vue'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import ItemBoxes from '../../molecules/item_boxes/ItemBoxes.vue'
import HorizontalScroll from '../../molecules/horizontal-scrolls/HorizontalScroll.vue'
import AccountIconWh40 from '../../atoms/account_icons/AccountIconWh40.vue'
import TextModalWithSecondary from '../../molecules/modals/with_secondary/text-modal-with-secondary.vue'
import BookmarkOutline from '../../atoms/svg_icons/BookmarkOutline.vue'
import BookmarkFillPrimary from '../../atoms/svg_icons/BookmarkFillPrimary.vue'
import HeartOutline from '../../atoms/svg_icons/HeartOutline.vue'
import HeartFillPrimairy from '../../atoms/svg_icons/HeartFillPrimairy.vue'
import ShareOutline from '../../atoms/svg_icons/ShareOutline.vue'
import ImgLazy from '../../atoms/images/ImgLazy.vue'
import { debounce } from 'lodash'
import PersonalBulkOrderSuggest from '../../../../../images/personal-bulk-order-suggest.png'
import RecommendBulkOrderModal from '../../organism/items/RecommendBulkOrderModal.vue'
import UsageInstructionModal from '../../organism/items/UsageInstructionModal.vue'
import LimitedTimeSalesInfo from '../../molecules/items/LimitedTimeSalesInfo.vue'
import CampaignNavigationArea from '../../organism/items/campaigns/CampaignNavigationArea.vue'
import GiftCodeCampaignDetailArea from '../../organism/items/campaigns/GiftCodeCampaignDetailArea.vue'
import CampaignAttentionModal from '../../organism/items/campaigns/CampaignAttentionModal.vue'
import StandardCampaignDetailArea from '../../organism/items/campaigns/StandardCampaignDetailArea.vue'
import SpecificationArea from '../../molecules/items/SpecificationArea.vue'
import NoticeArea from '../../molecules/items/NoticeArea.vue'
import GiftDetailArea from '../../molecules/items/GiftDetailArea.vue'
import DecorationHead from '../../atoms/decoration/DecorationHead.vue'
import PageNavigationBar from '../../molecules/items/PageNavigationBar.vue'
import { pageNavigationIds } from '../../../constants/pageNavigationConstants'

const DEBOUNCE_WAIT_MS = 500

export default {
  components: {
    SelectBoxes,
    SmallLoading,
    FeedbackSuccess,
    FeedbackError,
    ItemBoxes,
    HorizontalScroll,
    AccountIconWh40,
    TextModalWithSecondary,
    BookmarkOutline,
    BookmarkFillPrimary,
    HeartOutline,
    HeartFillPrimairy,
    ShareOutline,
    ImgLazy,
    RecommendBulkOrderModal,
    UsageInstructionModal,
    LimitedTimeSalesInfo,
    CampaignNavigationArea,
    GiftCodeCampaignDetailArea,
    CampaignAttentionModal,
    StandardCampaignDetailArea,
    SpecificationArea,
    NoticeArea,
    GiftDetailArea,
    DecorationHead,
    PageNavigationBar
  },
  props: {
    itemId: {
      type: String,
      required: true
    },
    loggedIn: {
      type: Boolean,
      required: true
    },
    hasProfile: {
      type: Boolean,
      required: true
    },
    brandId: {
      type: String,
      required: true
    },
    registrationPath: {
      type: String,
      required: true
    },
    loginPath: {
      type: String,
      required: true
    },
    bookmarkPath: {
      type: String,
      required: true
    },
    itemUrl: {
      type: String,
      required: true
    },
    selectedItemSkuId: {
      type: Number,
      required: false,
      default: 0
    },
    isWishListUserPresent: {
      type: Boolean,
      required: false,
      default: false
    },
    wishListUserId: {
      type: String,
      required: false,
      default: ''
    },
    wishListUserNickname: {
      type: String,
      required: false,
      default: ''
    },
    wishListUserIconUrl: {
      type: String,
      required: false,
      default: ''
    },
    wishListUserScreenName: {
      type: String,
      required: false,
      default: ''
    },
    wishListUrl: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      pageNavigationIds,
      isItemSkuLoading: true,
      itemSkus: [],
      itemSku: '',
      isMinusBtnActive: false,
      isPlusBtnActive: true,
      itemSkuCount: 1,
      showableSuccessFeedback: false,
      successMessage: '',
      showableErrorFeedback: false,
      errorMessage: '',
      isWishListLoading: false,
      isBrandLoading: true,
      isOtherItemsLoading: true,
      brand: '',
      brandItems: [],
      disableSendGiftButton: false,
      disablePersonalUseButton: false,
      isWishListModalActive: false,
      isLoginModalActive: false,
      isBulkOrderSuggestModalActive: false,
      isUsageInstructionModalActive: false,
      giftCodeCampaigns: [],
      isGiftCodeAttentionModalActive: false,
      currentGiftCodeCampaignAttention: '',
      standardCampaigns: [],
      isStandardCampaignAttentionModalActive: false,
      currentStandardCampaignAttention: ''
    }
  },
  computed: {
    shareUrl() {
      return `${this.itemUrl}/item_skus/${this.itemSku.id}`
    },
    bulkOrderSuggestImagePath() {
      return PersonalBulkOrderSuggest
    },
    itemSkuPrice() {
      return `¥${this.itemSku.price.toLocaleString()}`
    },
    selectBoxItemSkus() {
      const targetItemSkus = this.isWishListUserPresent
        ? this.itemSkus.filter(itemSku => itemSku.id === this.selectedItemSkuId)
        : this.itemSkus
      return targetItemSkus.map(itemSku => {
        return {
          name: itemSku.name,
          value: itemSku.id,
          is_out_of_stock: itemSku.isOutOfStock
        }
      })
    },
    selectedItemSkuIndex() {
      return this.selectBoxItemSkus.findIndex(v => v.value === this.itemSku.id)
    },
    hasSeveralItemSkus() {
      return this.itemSkus.length > 1
    },
    bookmarkButtonClass() {
      return this.generateActionButtonClass(this.itemSku.bookmarked)
    },
    wishListButtonClass() {
      return this.generateActionButtonClass(this.itemSku.wishListed)
    },
    showableOtherItemsSection() {
      return this.brandItems.length >= 1
    },
    isDisabledPaymentMethods() {
      return this.itemSku.disabledPaymentMethodNames.length !== 0
    },
    isEmptySelectedItemSkuId() {
      return this.selectedItemSkuId === 0
    },
    hasGiftCodeCampaigns() {
      return this.giftCodeCampaigns.length > 0
    },
    hasStandardCampaigns() {
      return this.standardCampaigns.length > 0
    },
    hasDescriptionItems() {
      return (
        this.itemSku.descriptionItems &&
        this.itemSku.descriptionItems.length > 0
      )
    },
    hasStaffReviews() {
      return this.itemSku.staffReviews && this.itemSku.staffReviews.length > 0
    }
  },
  mounted() {
    this.getItemSkus()
    this.getBrand()
    this.getOtherItems()
  },
  methods: {
    generateActionButtonClass(isPrimary) {
      return isPrimary
        ? 'items-show__action-button__text__primary'
        : 'items-show__action-button__text'
    },
    async getItemSkus() {
      try {
        const res = await axios.get(
          `/api/v1/pickup/items/${this.itemId}/item_skus?selected_item_sku_id=${this.selectedItemSkuId}`
        )
        this.itemSkus = res.data.data
          .map(itemSku => itemSku.attributes)
          .map(sku => {
            return {
              id: sku.id,
              name: sku.name,
              imageUrl: sku.imageUrl,
              brandName: sku.brandName,
              price: sku.price,
              isIncludedTax: sku.isIncludedTax,
              expiredDate: sku.expiredDate,
              usageInstructionTitle: sku.usageInstructionTitle,
              usageInstructionDescription: sku.usageInstructionDescription,
              isOutOfStock: sku.isOutOfStock,
              bookmarked: sku.bookmarked,
              bookmarkId: sku.bookmarkId,
              serverBookmarked: sku.bookmarked,
              wishListed: sku.wishListed,
              wishListId: sku.wishListId,
              serverWishListed: sku.wishListed,
              descriptionHtml: sku.descriptionHtml,
              specificationHtml: sku.specificationHtml,
              instructionHtml: sku.instructionHtml,
              disabledPaymentMethodNames: sku.disabledPaymentMethodNames,
              itemName: sku.itemName,
              quantityLimit: sku.quantityLimit,
              limitedTimeSalesEndedAt: sku.limitedTimeSalesEndedAt,
              giftCodeCampaigns: sku.gift_code_campaigns || [],
              standardCampaigns: sku.standard_campaigns || [],
              descriptionItems: sku.descriptionItems || [],
              mailSlotDeliverable: sku.mailSlotDeliverable || false,
              staffReviews: sku.staffReviews || [],
              setContents: sku.setContents || '',
              capacitySizeWeight: sku.capacitySizeWeight || '',
              otherSpecifications: sku.otherSpecifications || '',
              foodAndBeverageDetail: sku.foodAndBeverageDetail || null,
              noticeDetail: sku.noticeDetail || null
            }
          })
        this.itemSku =
          this.isEmptySelectedItemSkuId ||
          !this.itemSkus.some(v => v.id === this.selectedItemSkuId)
            ? this.itemSkus[0]
            : this.itemSkus.find(v => v.id === this.selectedItemSkuId)
        this.isPlusBtnActive =
          !this.itemSku.isOutOfStock && this.itemSku.quantityLimit !== 1
        this.disableSendGiftButton = this.itemSku.isOutOfStock
        this.disablePersonalUseButton = this.itemSku.isOutOfStock
        this.giftCodeCampaigns = this.itemSku.giftCodeCampaigns || []
        this.standardCampaigns = this.itemSku.standardCampaigns || []
      } catch (error) {
        this.errorMessage =
          error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
      }
      this.isItemSkuLoading = false
    },
    countDown() {
      if (this.itemSku.isOutOfStock) return
      this.itemSkuCount = this.itemSkuCount - 1
      if (this.itemSkuCount <= 1) {
        this.isMinusBtnActive = false
        if (this.itemSku.quantityLimit > 1) {
          this.isPlusBtnActive = true
        }

        this.itemSkuCount = 1
        return
      }
      if (
        1 < this.itemSkuCount &&
        this.itemSkuCount <= this.itemSku.quantityLimit
      ) {
        this.isPlusBtnActive = true
        this.isMinusBtnActive = true
      }
    },
    countUp() {
      if (this.itemSku.isOutOfStock) return
      this.itemSkuCount = this.itemSkuCount + 1
      if (this.itemSkuCount >= this.itemSku.quantityLimit) {
        this.itemSkuCount = this.itemSku.quantityLimit
        this.isPlusBtnActive = false
        if (this.itemSku.quantityLimit > 1) {
          this.isMinusBtnActive = true
        }

        return
      }
      if (
        1 < this.itemSkuCount &&
        this.itemSkuCount < this.itemSku.quantityLimit
      ) {
        this.isPlusBtnActive = true
        this.isMinusBtnActive = true
      }
    },
    selectRadioBtn(value) {
      const itemSku = this.itemSkus.find(
        itemSku => itemSku.id === Number(value)
      )
      this.itemSku = itemSku
      this.itemSkuCount = 1
      this.isMinusBtnActive = false
      this.isPlusBtnActive =
        !itemSku.isOutOfStock && itemSku.quantityLimit !== 1
      this.disableSendGiftButton = this.itemSku.isOutOfStock
      this.disablePersonalUseButton = this.itemSku.isOutOfStock
      this.giftCodeCampaigns = this.itemSku.giftCodeCampaigns || []
      this.standardCampaigns = this.itemSku.standardCampaigns || []
    },
    enterSuccessFeedback() {
      setTimeout(() => {
        this.showableSuccessFeedback = false
      }, 3000)
    },
    enterErrorFeedback() {
      setTimeout(() => {
        this.showableErrorFeedback = false
      }, 3000)
    },
    onClickShareButton() {
      dataLayer.push({
        event: 'click_item_detail__share_url',
        item_sku_id: this.itemSku.id
      })

      if (navigator.canShare) {
        navigator.share({
          url: this.shareUrl
        })
        return
      }

      const result = navigator.clipboard.writeText(this.shareUrl)
      if (result) {
        this.successMessage = `URLをコピーしました`
        this.showableSuccessFeedback = true
      } else {
        this.errorMessage = `URLのコピーに失敗しました`
        this.showableErrorFeedback = true
      }
    },
    toggleBookmarkAction() {
      if (this.loggedIn) {
        this.itemSku.bookmarked ? this.removeBookmark() : this.addBookmark()
      } else {
        location.href = this.bookmarkPath
      }
    },
    toggleWishListAction() {
      if (this.loggedIn) {
        this.itemSku.wishListed ? this.removeWishList() : this.addWishList()
      } else {
        this.isLoginModalActive = true
      }
    },
    openBulkOrderSuggestModal() {
      this.isBulkOrderSuggestModalActive = true
    },
    async addBookmark() {
      this.itemSku.bookmarked = true
      await this.toggleBookmark()
    },
    async removeBookmark() {
      this.itemSku.bookmarked = false
      await this.toggleBookmark()
    },
    toggleBookmark: debounce(async function() {
      // ボタンを押した結果がサーバーの状態と同じ場合は何もしない
      if (this.itemSku.bookmarked === this.itemSku.serverBookmarked) return

      if (this.itemSku.bookmarked) {
        await this.addBookmarkReq()
      } else {
        await this.removeBookmarkReq()
      }
    }, DEBOUNCE_WAIT_MS),
    async addBookmarkReq() {
      dataLayer.push({
        event: 'click_item_detail__bookmark',
        item_sku_id: this.itemSku.id
      })

      try {
        const res = await axios.post('/api/v1/users/@me/item_sku_bookmarks', {
          bookmark: { item_sku_id: this.itemSku.id }
        })

        this.itemSku.serverBookmarked = true
        this.itemSku.bookmarkId = res.data.bookmark_id
        this.successMessage = `ブックマークに追加しました`
        this.showableSuccessFeedback = true
      } catch (error) {
        this.itemSku.serverBookmarked = false
        this.itemSku.bookmarked = false
        this.errorMessage = `ブックマークの追加に失敗しました`
        this.showableErrorFeedback = true
      }
    },
    async removeBookmarkReq() {
      dataLayer.push({
        event: 'click_item_detail__unbookmark',
        item_sku_id: this.itemSku.id
      })

      try {
        await axios.delete(
          `/api/v1/users/@me/item_sku_bookmarks/${this.itemSku.bookmarkId}`
        )

        this.itemSku.serverBookmarked = false
        this.itemSku.bookmarkId = null
        this.successMessage = `ブックマークから削除しました`
        this.showableSuccessFeedback = true
      } catch (error) {
        this.itemSku.serverBookmarked = true
        this.itemSku.bookmarked = true
        this.errorMessage = `ブックマークの削除に失敗しました`
        this.showableErrorFeedback = true
      }
    },
    async addWishList() {
      this.itemSku.wishListed = true
      await this.toggleWishList()
    },
    async removeWishList() {
      this.itemSku.wishListed = false
      await this.toggleWishList()
    },
    toggleWishList: debounce(async function() {
      // ボタンを押した結果がサーバーの状態と同じ場合は何もしない
      if (this.itemSku.wishListed === this.itemSku.serverWishListed) return

      if (this.itemSku.wishListed) {
        await this.addWishListReq()
      } else {
        await this.removeWishListReq()
      }
    }, DEBOUNCE_WAIT_MS),
    async addWishListReq() {
      dataLayer.push({
        event: 'add_to_wishlist',
        item_sku_id: this.itemSku.id,
        item_sku_name: this.itemSku.name
      })

      try {
        const res = await axios.post('/api/v1/wish_lists', {
          item_sku_id: this.itemSku.id
        })
        this.itemSku.wishListId = res.data.wish_list_id
        this.itemSku.serverWishListed = true
        this.successMessage = `ほしいものリストに追加しました`
        this.showableSuccessFeedback = true
      } catch (error) {
        this.itemSku.serverWishListed = false
        this.itemSku.wishListed = false
        this.errorMessage = `ほしいものリストの追加に失敗しました`
        this.showableErrorFeedback = true
      }
    },
    async removeWishListReq() {
      try {
        await axios.delete(`/api/v1/wish_lists/${this.itemSku.wishListId}`)
        this.itemSku.wishListId = null
        this.itemSku.serverWishListed = false
        this.successMessage = `ほしいものリストから削除しました`
        this.showableSuccessFeedback = true
      } catch (error) {
        this.itemSku.serverWishListed = true
        this.itemSku.wishListed = true
        this.errorMessage = `ほしいものリストの削除に失敗しました`
        this.showableErrorFeedback = true
      }
    },
    async submitSendGiftButton() {
      this.disablePurchaseButtons()

      if (this.isWishListUserPresent) {
        await this.submitSendGiftButtonWithWishList()
      } else {
        await this.sendGiftWithoutWishList()
      }
    },
    async sendGiftWithoutWishList() {
      dataLayer.push({
        event: 'add_to_cart',
        item_sku_id: this.itemSku.id,
        ecommerce: {
          items: [
            {
              item_id: this.itemSku.id,
              quantity: this.itemSkuCount,
              item_list_name: 'item_detail'
            }
          ]
        }
      })

      try {
        await axios.post(`/api/v1/pickup/gift_card_item_sku_contents/create`, {
          item_sku_form: {
            item_id: this.itemId,
            item_sku_id: this.itemSku.id,
            quantity: this.itemSkuCount,
            submit_type: 'send_gift'
          }
        })
        location.href = '/gift_card/edit'
      } catch (error) {
        this.errorMessage =
          'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
        this.enablePurchaseButtons()
      }
    },
    disablePurchaseButtons() {
      this.disableSendGiftButton = true
      this.disablePersonalUseButton = true
    },
    enablePurchaseButtons() {
      this.disableSendGiftButton = false
      this.disablePersonalUseButton = false
    },
    togglePersonalUseButton() {
      if (this.loggedIn) {
        this.submitPersonalUseButton()
        return
      }
      this.moveToRegistration()
    },
    async submitPersonalUseButton() {
      this.disablePurchaseButtons()
      try {
        const res = await axios.post(
          `/api/v1/pickup/gift_card_item_sku_contents/create`,
          {
            item_sku_form: {
              item_id: this.itemId,
              item_sku_id: this.itemSku.id,
              quantity: this.itemSkuCount,
              submit_type: 'personal_use'
            }
          }
        )
        location.href = `/draft_orders/${res.data.draft_order_id}`
      } catch (error) {
        this.errorMessage =
          'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
        this.enablePurchaseButtons()
      }
    },
    moveToRegistration() {
      location.href = this.registrationPath
    },
    moveToLogin() {
      location.href = this.loginPath
    },
    async getBrand() {
      try {
        const res = await axios.get(`/api/v1/pickup/brands/${this.brandId}`)
        this.brand = {
          name: res.data.data.attributes.name,
          imageUrl: res.data.data.attributes.icon_image_url,
          description: res.data.data.attributes.description,
          listDisplayableItemPresent:
            res.data.data.attributes.list_displayable_item_present
        }
      } catch (error) {
        this.errorMessage =
          error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
      }
      this.isBrandLoading = false
    },
    async getOtherItems() {
      try {
        const res = await axios.get(
          `/api/v1/pickup/brands/${this.brandId}/items`,
          {
            params: {
              excluded_item_id: this.itemId,
              limit: 30
            }
          }
        )
        this.brandItems = res.data.data
          .map(item => item.attributes)
          .map(item => {
            return {
              id: item.id,
              linkUrl: `/items/${item.id}`,
              imageUrl: item.item_box_image_url,
              brandName: item.brand_name,
              itemName: item.name,
              itemPrice: item.min_price.toLocaleString(),
              isIncludingTax: item.is_including_tax,
              hasMultipleItemSkus: item.item_sku_count > 1,
              soldOut: item.sold_out,
              labels: item.labels || []
            }
          })
      } catch (error) {
        this.errorMessage =
          error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
      }
      this.isOtherItemsLoading = false
    },
    moveToBrandDetail() {
      location.href = `/brands/${this.brandId}`
    },
    onMountedSelectBoxes() {
      const horizontal = this.$refs['select-box-horizontal']
      horizontal.initializeValues()
      // NOTE: 横スクロール量 = (横スクロール部分の全長 / itemSkuの数) * 選択されたitemSkuのindex
      horizontal.scrollTo(
        (horizontal.scrollBoxScrollWidth / this.selectBoxItemSkus.length) *
          this.selectedItemSkuIndex
      )
    },
    onClickBackToTop() {
      window.scrollTo({ top, behavior: 'smooth' })
    },
    closeWishListModal() {
      this.enablePurchaseButtons()
      this.isWishListModalActive = false
    },
    closeLoginModal() {
      this.isLoginModalActive = false
    },
    closeBulkOrderSuggestModal() {
      this.isBulkOrderSuggestModalActive = false
    },
    openUsageInstructionModal() {
      this.isUsageInstructionModalActive = true
    },
    closeUsageInstructionModal() {
      this.isUsageInstructionModalActive = false
    },
    openGiftCodeAttentionModal(data) {
      this.currentGiftCodeCampaignAttention = data.attention
      this.isGiftCodeAttentionModalActive = true
    },
    closeGiftCodeAttentionModal() {
      this.isGiftCodeAttentionModalActive = false
      this.currentGiftCodeCampaignAttention = ''
    },
    openStandardCampaignAttentionModal(data) {
      this.currentStandardCampaignAttention = data.attention
      this.isStandardCampaignAttentionModalActive = true
    },
    closeStandardCampaignAttentionModal() {
      this.isStandardCampaignAttentionModalActive = false
      this.currentStandardCampaignAttention = ''
    },
    handleCopySuccess(message) {
      this.successMessage = message
      this.showableSuccessFeedback = true
    },
    handleCopyError(message) {
      this.errorMessage = message
      this.showableErrorFeedback = true
    },
    moveToGiftCardEditPage() {
      location.href = '/gift_card/edit'
      this.isWishListModalActive = false
    },
    async submitSendGiftButtonWithWishList() {
      const isDifferentUserAsGiftCardRecipient = await this.getWishListUserGiftCardEditingStatus()
      if (isDifferentUserAsGiftCardRecipient) {
        this.isWishListModalActive = true
        return
      }
      this.createGiftCardWithWishList()
    },
    async getWishListUserGiftCardEditingStatus() {
      try {
        const res = await axios.get(
          `/api/v1/pickup/gift_card_sessions/edit_status?screen_name=${this.wishListUserScreenName}`
        )
        return res.data.edit_status
      } catch (error) {
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
        this.enablePurchaseButtons()
      }
    },
    async createGiftCardWithWishList() {
      dataLayer.push({
        event: 'add_to_cart',
        item_sku_id: this.itemSku.id,
        recipient_user_id: this.wishListUserId,
        ecommerce: {
          items: [
            {
              item_id: this.itemSku.id,
              quantity: this.itemSkuCount,
              item_list_name: 'wishlist_detail'
            }
          ]
        }
      })

      try {
        const result = await axios.post(
          '/api/v1/pickup/gift_card_session_item_sku_contents',
          {
            item_sku_form: {
              item_id: this.itemId,
              item_sku_id: this.itemSku.id,
              quantity: this.itemSkuCount,
              screen_name: this.wishListUserScreenName,
              from_item_show: true
            }
          }
        )
        const path_params = result.data.wish_list_user_changed
          ? '?from_item_show=true&wish_list_changed=true'
          : '?from_item_show=true'
        location.href = `/gift_card/edit${path_params}`
      } catch (error) {
        this.errorMessage =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'システムエラーが発生しました。しばらく経ってから再度アクセスしてください'
        this.showableErrorFeedback = true
        this.enablePurchaseButtons()
      }
    }
  }
}
</script>
<style lang="scss" scoped>
.items-show {
  &__wish-list-user-area {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
  }

  &__wish-list-user-icon {
    margin-right: 8px;
  }

  &__wish-list-user-nickname {
    @extend %body-bold;
  }

  &__wish-list-user-for-label {
    @extend %body;
    line-height: 1.6;
  }

  &__back-to-wish-list-area {
    margin-top: 8px;
  }

  &__back-to-wish-list-link {
    display: block;
  }

  &__action-button-area {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    cursor: pointer;
  }

  &__action-button {
    @extend %minimal;
    width: 64px;

    &__loading {
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
    }

    &__icon {
      display: flex;
      justify-content: center;
      margin-bottom: 4px;
    }

    &__text {
      display: block;
      text-align: center;

      &__primary {
        display: block;
        text-align: center;
        color: $primary;
      }
    }
  }

  &__bulk-order-sub-content {
    margin: 0 auto;
    padding: 16px 24px;
    box-sizing: border-box;
    max-width: $base-contents-width;

    @media screen and (max-width: ($base-contents-width - 1)) {
      max-width: $small-contents-width;
      padding: 16px;
    }
  }

  &__bulk-order-area {
    background-color: #f7e9e8;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 16px;

    &--icon {
      width: 128px;
      object-fit: contain;

      @media screen and (max-width: ($base-contents-width - 1)) {
        width: 80px;
      }
    }

    &--text {
      flex: 1;
      font-size: 1.2rem;
      line-height: 1.6;
      font-weight: bold;
      color: $brown-1;
    }

    &--text-link {
      text-decoration: underline;
      cursor: pointer;
    }
  }

  &__navigation-wrapper {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__campaign-detail-wrapper {
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 24px 0 32px;
  }

  &__gift-code-campaign-detail-wrapper {
    padding: 0 24px;
    box-sizing: border-box;
    margin: auto;
    max-width: 768px;
    width: 100%;

    @media all and (max-width: ($base-contents-width - 1)) {
      padding: 0 16px;
    }
  }

  &__standard-campaign-detail-wrapper {
    padding: 0 24px;
    box-sizing: border-box;
    margin: auto;
    max-width: 768px;
    width: 100%;

    @media all and (max-width: ($base-contents-width - 1)) {
      padding: 0 16px;
    }
  }
}
</style>
